import { fetchApi, fetchPatientApi } from '../client';

export type FeedbackStatus = 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'ALREADY_SUBMITTED';
export type FeedbackCategory = 'PROMOTER' | 'PASSIVE' | 'DETRACTOR';

export interface FeedbackPublicView {
  feedbackId: string;
  feedbackToken: string;
  status: FeedbackStatus;
  clinicName?: string;
  doctorName?: string;
  visitDate?: string;
  /** The clinic accepts public reviews — show the optional review section. */
  reviewsEnabled?: boolean;
  reviewConsentVersion?: string;
  /** After submission: state of the public review, if one was written. */
  reviewStatus?: 'PENDING' | 'PUBLISHED' | 'REJECTED' | 'WITHDRAWN' | 'ERASED';
}

/** Optional public review. Separate from the private score and comment, which are never published. */
export interface PublicReviewInput {
  recommends: boolean;
  text: string;
  showName: boolean;
  consent: boolean;
}

export interface FeedbackSubmitRequest {
  npsScore: number;
  comment?: string;
  publicReview?: PublicReviewInput;
}

export interface FeedbackSummary {
  npsScore: number;
  averageScore: number;
  totalResponses: number;
  promoterCount: number;
  passiveCount: number;
  detractorCount: number;
  responseRatePct: number;
  periodDays: number;
}

export interface FeedbackTrendPoint {
  month: string;
  label: string;
  npsScore: number;
  totalResponses: number;
}

export interface FeedbackReview {
  feedbackId: string;
  doctorName?: string;
  npsScore: number;
  category: FeedbackCategory;
  comment?: string;
  submittedAt: string;
}

export interface PagedFeedbackReviews {
  content: FeedbackReview[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const feedbackModule = {
  // ── Public (patient-facing, no auth) ────────────────────────────────────
  getPublicView: (token: string) =>
    fetchPatientApi<FeedbackPublicView>(`/api/public/feedback/${token}`, { skipAuth: true }),

  /** The patient removes their public review using the same private link. */
  withdrawReview: (token: string) =>
    fetchPatientApi<void>(`/api/public/feedback/${token}/review/withdraw`, { method: 'POST', skipAuth: true }),

  submit: (token: string, data: FeedbackSubmitRequest) =>
    fetchPatientApi<{ submitted: boolean }>(`/api/public/feedback/${token}`, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify(data),
    }),

  // ── Analytics (clinic_admin clinic-wide, doctor own-scope) ──────────────
  /** from/to (yyyy-MM-dd, inclusive) take precedence over days (medopd-docs 136 §3). */
  getSummary: (params: { doctorId?: string; days?: number; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.doctorId) qs.set('doctorId', params.doctorId);
    if (params.days !== undefined) qs.set('days', String(params.days));
    if (params.from && params.to) { qs.set('from', params.from); qs.set('to', params.to); }
    return fetchApi<FeedbackSummary>(`/api/feedback/summary?${qs}`);
  },

  getTrend: (params: { doctorId?: string; months?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.doctorId) qs.set('doctorId', params.doctorId);
    if (params.months !== undefined) qs.set('months', String(params.months));
    return fetchApi<FeedbackTrendPoint[]>(`/api/feedback/trend?${qs}`);
  },

  getReviews: (params: { doctorId?: string; minScore?: number; maxScore?: number; page?: number; size?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.doctorId) qs.set('doctorId', params.doctorId);
    if (params.minScore !== undefined) qs.set('minScore', String(params.minScore));
    if (params.maxScore !== undefined) qs.set('maxScore', String(params.maxScore));
    qs.set('page', String(params.page ?? 0));
    qs.set('size', String(params.size ?? 20));
    return fetchApi<PagedFeedbackReviews>(`/api/feedback/reviews?${qs}`);
  },
};
